import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Badge, { BadgeBg } from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faColumns } from '@fortawesome/free-solid-svg-icons';
import { currencyFormat } from 'helpers/utils';

// Import ShippingMethod enum
export enum ShippingMethod {
  FBW = 16500, // Fulfilled By Wamia
  ESS = 16501, // Easy Shipping By Seller
  ESW = 18383, // Easy Shipping By Wamia
}

// Export commission calculation result type
export type CommissionCalculation = {
  fixCommission: number;
  percentCommission: number; 
  percentCommissionValue: number;
  applicablePrice: number;
  totalCommission: number;
  isSpecialPriceApplicable: boolean;
};

// Define product type for commission modal
export type ProductCommissionDetails = {
  id: string;
  name: string;
  sku: string;
  price: number;
  specialPrice?: number | null;
  discountStartDate?: string | null;
  discountEndDate?: string | null;
  status: number;
  product_status: number; // Used in modal
  productStatus?: number; // Used in some components
  productImage: string;
  shippingMethod?: number;
  dimensions?: {
    longueur?: number | string;
    largeur?: number | string;
    hauteur?: number | string;
  };
  weight?: number | string;
  totalSold?: number;
  // Add fields needed for commission calculation
  fixCommission?: number;
  percentCommission?: number;
};

interface CommissionDetailsModalProps {
  show: boolean;
  onHide: () => void;
  product: ProductCommissionDetails | null;
  calculateCommission: (product: ProductCommissionDetails) => CommissionCalculation;
}

const CommissionDetailsModal = ({ 
  show, 
  onHide, 
  product, 
  calculateCommission 
}: CommissionDetailsModalProps) => {
  const { t } = useTranslation();
  if (!product) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg" className="commission-details-modal bg-body">
        <Modal.Header closeButton className="border-0 pb-0 pt-3 bg-body">
          <Modal.Title>{t('products.commission.title', 'Commission Details')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-body">
          <div className="text-center py-5">
            <div className="mb-3">
              <span className="fa-stack fa-2x">
                <FontAwesomeIcon icon={faColumns} className="text-secondary opacity-25 fa-stack-2x" />
              </span>
            </div>
            <p className="fs-5 text-body">{t('products.commission.noDetails', 'No commission details available')}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 bg-body">
          <Button variant="primary" className="px-4" onClick={onHide}>
            {t('common.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Get product status for the modal
  const productStatus = product.product_status || product.productStatus || 0;
  
  // Calculate commission details
  const commission = calculateCommission(product);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="commission-details-modal">
      <Modal.Header closeButton className="border-0 pb-0 pt-3 bg-body">
        <Modal.Title className="d-flex align-items-center">
          <div className="d-flex">
            <img 
              src={product.productImage} 
              alt={product.name}
              width="40" 
              height="40" 
              className="border rounded me-3"
              style={{ objectFit: 'contain' }}
            />
            <div>
              <h5 className="mb-0 text-body">{t('products.commission.title', 'Commission Details')}</h5>
              <div className="text-muted small">
                {product.name} 
                <span className="badge bg-body-tertiary text-body ms-2">
                  {t('products.sku', 'SKU')}: {product.sku}
                </span>
              </div>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 bg-body">
        <div>
          <div className="commission-details">
            {/* Status badges row */}
            <div className="d-flex gap-2 mb-3 justify-content-end">
              <Badge 
                bg={product.status === 1 ? 'success' : 'danger'} 
                variant="phoenix"
              >
                {product.status === 1 ? t('products.status.onSale', 'On Sale') : t('products.status.closed', 'Close for Sale')}
              </Badge>
              {(() => {
                const badgeVariant: BadgeBg = 
                  productStatus === 1 ? 'success' : 
                  productStatus === 3 ? 'danger' : 
                  'warning';
                return (
                  <Badge 
                    bg={badgeVariant} 
                    variant="phoenix"
                  >
                    {productStatus === 1 
                      ? t('products.status.approved', 'Approved') 
                      : productStatus === 3 
                        ? t('products.status.rejected', 'Rejected') 
                        : t('products.status.disapproved', 'Disapproved')}
                  </Badge>
                );
              })()}
            </div>
            
            {/* Cards row */}
            <div className="row g-3 mb-4">
              {/* Price card */}
              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm bg-body">
                  <div className="card-header bg-body py-2 border-0">
                    <h5 className="mb-0 fw-bold text-body">{t('products.price.title', 'Price Information')}</h5>
                  </div>
                  <div className="card-body pb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <span className="text-body">{t('products.price.regular', 'Regular Price')}</span>
                      <span className="fw-bold text-body">{Number(product.price || 0).toFixed(2)} TND</span>
                    </div>
                    
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-body">{t('products.price.special', 'Special Price')}</span>
                        {product.specialPrice ? (
                          <span className={`fw-bold ${commission.isSpecialPriceApplicable ? "text-success" : "text"}`}>
                            {Number(product.specialPrice || 0).toFixed(2)} TND
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </div>
                      
                      {product.specialPrice && product.discountStartDate && product.discountEndDate && (
                        <div className="mt-2 p-2 bg-body-tertiary rounded">
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">{t('products.from', 'From')}:</span>
                            <span className="text-body">{new Date(product.discountStartDate).toLocaleDateString("fr-FR")}</span>
                          </div>
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">{t('products.to', 'To')}:</span>
                            <span className="text-body">{new Date(product.discountEndDate).toLocaleDateString("fr-FR")}</span>
                          </div>
                          <div className="d-flex justify-content-between small">
                            <span className="text-muted">{t('products.statuslabel', 'Status')}:</span>
                            <span className={commission.isSpecialPriceApplicable ? "text-success fw-bold" : "text-danger"}>
                              {commission.isSpecialPriceApplicable ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* {product.totalSold !== undefined && (
                      <div className="mt-3 pt-2 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-body">{t('products.totalSold', 'Total Sold')}</span>
                          <span className="fw-bold text-success">{product.totalSold}</span>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>
              </div>
              
              {/* Commission card */}
              <div className="col-md-6">
                <div className="card h-100 border-0 shadow-sm bg-body">
                  <div className="card-header bg-body py-2 border-0">
                    <h6 className="mb-0 fw-bold text-body">{t('products.commission.info', 'Commission Information')}</h6>
                  </div>
                  <div className="card-body pb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                      <span className="text-body">{t('products.commission.fixCommission', 'Fix Commission')}</span>
                      <span className="fw-bold text-body">{commission.fixCommission.toFixed(2)} TND</span>
                    </div>
                    
                    {/* Fixed Commission calculation explanation with actual values */}
                    {product.dimensions && product.shippingMethod && (
                      <div className="bg-body-tertiary p-3 rounded mb-3 small">
                        {(() => {
                          // Get product dimensions and weight
                          const length = parseFloat(String(product.dimensions.longueur)) || 0;
                          const width = parseFloat(String(product.dimensions.largeur)) || 0;
                          const height = parseFloat(String(product.dimensions.hauteur)) || 0;
                          const weight = parseFloat(String(product.weight)) || 0;
                          
                          // Calculate volumetric weight
                          const volumetricWeight = (length * width * height) / 5000;
                          
                          // Use the greater of actual weight or volumetric weight
                          const taxableWeight = Math.max(volumetricWeight, weight);
                          
                          // Determine formula based on shipping method
                          let formula = "";
                          let baseAmount = 0;
                          let coefficient = 0;
                          let methodName = "";
                          
                          switch(Number(product.shippingMethod)) {
                            case ShippingMethod.FBW:
                              methodName = "Fulfilled By Wamia";
                              baseAmount = 1;
                              coefficient = 0.6;
                              formula = `${baseAmount} + (${taxableWeight.toFixed(3)} × ${coefficient})`;
                              break;
                            case ShippingMethod.ESS:
                              methodName = "Easy Shipping By Seller";
                              baseAmount = 1;
                              coefficient = 0.5;
                              formula = `${baseAmount} + (${taxableWeight.toFixed(3)} × ${coefficient})`;
                              break;
                            case ShippingMethod.ESW:
                              methodName = "Easy Shipping By Wamia";
                              baseAmount = 1.5;
                              coefficient = 0.6;
                              formula = `${baseAmount} + (${taxableWeight.toFixed(3)} × ${coefficient})`;
                              break;
                            default:
                              methodName = "Unknown";
                              formula = "Unknown calculation method";
                          }
                          
                          const calculatedValue = baseAmount + (taxableWeight * coefficient);
                          
                          return (
                            <>
                              <p className="mb-2">
                                <span className="fw-bold">{t('products.shippingMethod', 'Shipping method')}:</span> <span className="text-secondary">{methodName}</span>
                              </p>
                              <p className="mb-2">
                                <span className="fw">{t('products.commission.volumetricWeight', 'Volumetric weight')}:</span> <span className="text-primary">({length} × {width} × {height}) ÷ 5000 = {volumetricWeight.toFixed(2)}</span>
                              </p>
                              <p className="mb-2">
                                <span className="fw">{t('products.commission.taxableWeight', 'Taxable weight')}:</span> <span className="text-primary">{t('products.commission.maxBetween', 'max between')} ({volumetricWeight.toFixed(3)}, {weight}) = {taxableWeight.toFixed(2)} kg</span>
                              </p>
                              
                              <p className="mb-2">
                                <span className="fw">{t('products.commission.fixValue', 'Fix Value')}:</span> <span className="text-success">{formula} = {calculatedValue.toFixed(2)} TND</span>
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-body">{t('products.commission.percentCommission', 'Percent Commission')}</span>
                      <span className="fw-bold text-body">{commission.percentCommission}%</span>
                    </div>
                    
                    <div className="p-2 bg-body-tertiary rounded">
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">{t('products.commission.percentValue', 'Percent Value')}:</span>
                        <span className="text-success">
                          {commission.percentCommission}% × {Number(commission.applicablePrice).toFixed(2)} TND = {commission.percentCommissionValue.toFixed(2)} TND
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Total commission summary */}
              <div className="card border-0 shadow-sm mb-2">
              <div className="card-body p-3 bg-primary-subtle">
                <div className="text-center">
                  <h6 className="mb-3 text-body fw-bold">{t('products.commission.total', 'Total Commission')}</h6>
                  
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <span className="text fw-bold me-1">{commission.fixCommission.toFixed(2)}</span>
                      <span className="text-muted mx-2">+</span>
                      <span className="text fw-bold me-1">{commission.percentCommissionValue.toFixed(2)}</span>
                      <span className="text-muted mx-2">=</span>
                      <span className="text-success fs-4 fw-bold">{commission.totalCommission.toFixed(2)} TND</span>
                    </div>
                  </div>
                  
                          <div className="small text-muted mt-2">
                            {t('products.commission.usingPrice', 'Using {{type}} price', {
                              type: commission.isSpecialPriceApplicable ? t('products.price.special', 'special') : t('products.price.regular', 'regular')
                            })}: {Number(commission.applicablePrice).toFixed(2)} TND
                          </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 bg-body">
        <Button variant="primary" className="px-4" onClick={onHide}>
          {t('common.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommissionDetailsModal;
